import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiPaths } from '../../../api';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { ICoursesForSubscriptions, ICourseStudent } from '.';

export type TUpdateCourseStudent = {
  id: string;
  autoTopupEnabled?: boolean;
  autoTopupHours?: number;
};

/**
 * Auto lesson analysis opt-in. Send exactly one identifier — `courseId` from the
 * checkout / course page, or `courseStudentId` where the enrolment id is known.
 */
export type TUpdateCourseStudentAutoAnalysis = {
  courseId?: string;
  courseStudentId?: string;
  autoAnalysis: boolean;
};

export type TUpdateCourseStudentLearningDetails = {
  courseStudentId: string;
  subjectExperienceLevel?: string;
  lastExamStatus?: string;
  specificTopic?: string;
  currentGrade?: string;
  targetGrade?: string;
  upcomingExamDate?: string;
  learningChallenges?: string;
  examConfidenceLevel?: string;
  lessonStylePreferences?: string;
  specialRequirement?: string;
  difficultTopics?: string;
  skippedOnboarding?: boolean;
  onboardingCompleted?: boolean;
};

const apiDataProvider = new APIDataProvider();

export const getCoursesByUser = createAsyncThunk(
  '/course-students/getCoursesByUser',
  async () => {
    const path = `${ApiPaths.courseStudents}/status`;

    const response = await apiDataProvider.get<
      TApiResponse<ICoursesForSubscriptions>
    >(path);

    return response.data;
  }
);

export const getCourseStudentsByCourseId = createAsyncThunk(
  '/course-students/getCourseStudentsByCourseId',
  async (id: string) => {
    const path = `${ApiPaths.courseStudents}/${id}`;

    const response = await apiDataProvider.get<
      TApiResponse<Array<ICourseStudent>>
    >(path);

    return response.data;
  }
);

export const getCourseStudentsByUser = createAsyncThunk(
  '/course-students/getCourseStudentsByUser',
  async (id: string) => {
    const path = `${ApiPaths.courseStudents}/student/${id}`;

    const response = await apiDataProvider.get<
      TApiResponse<Array<ICourseStudent>>
    >(path);

    return response.data;
  }
);

export const updateCourseStudentLearningDetails = createAsyncThunk(
  '/course-students/update-learning-details',
  async (data: TUpdateCourseStudentLearningDetails) => {
    const path = `${ApiPaths.courseStudents}/learning-details`;

    const response = await apiDataProvider.put<TApiResponse<ICourseStudent>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateCourseStudentAutoAnalysis = createAsyncThunk(
  '/course-students/updateAutoAnalysis',
  async (data: TUpdateCourseStudentAutoAnalysis, { rejectWithValue }) => {
    try {
      const path = `${ApiPaths.courseStudents}/auto-analysis`;

      const response = await apiDataProvider.put<TApiResponse<ICourseStudent>>(
        path,
        data
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateCourseStudent = createAsyncThunk(
  '/course-students/updateCourseStudent',
  async (data: TUpdateCourseStudent) => {
    const path = ApiPaths.courseStudents;

    const response = await apiDataProvider.put<TApiResponse<ICourseStudent>>(
      path,
      data
    );

    return response.data;
  }
);
